import { useState } from 'react';
import { MessageSquare, X, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LiveChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API_URL}/api/chat/messages`, formData);
      toast.success('Message sent! We\'ll get back to you soon.');
      setIsSubmitted(true);
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const resetChat = () => {
    setIsSubmitted(false);
    setFormData({ name: '', email: '', message: '' });
  };

  return (
    <>
      {/* Chat Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="live-chat-toggle"
        className={`fixed bottom-24 right-6 z-40 w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 ${
          isOpen ? 'bg-[#FF5E1A] rotate-90' : 'bg-[#0A1121] border border-white/10 hover:border-[#FF5E1A]/40'
        }`}
        aria-label="Toggle live chat"
      >
        {isOpen ? (
          <X size={24} className="text-white" />
        ) : (
          <MessageSquare size={24} className="text-[#FF5E1A]" />
        )}
      </button>

      {/* Chat Widget */}
      {isOpen && (
        <div
          data-testid="live-chat-widget"
          className="fixed bottom-44 right-6 z-40 w-80 bg-[#0A1121] rounded-2xl border border-white/10 shadow-2xl chat-widget overflow-hidden"
        >
          {/* Header */}
          <div className="bg-[#FF5E1A] px-4 py-3">
            <h3 className="text-white font-semibold">Live Chat</h3>
            <p className="text-white/80 text-xs">We typically reply within minutes</p>
          </div>

          {/* Body */}
          <div className="p-4">
            {isSubmitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-[#FF5E1A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageSquare size={32} className="text-[#FF5E1A]" />
                </div>
                <h4 className="text-white font-semibold mb-2">Message Sent!</h4>
                <p className="text-[#A1B0CC] text-sm mb-4">
                  We&apos;ll get back to you as soon as possible.
                </p>
                <button
                  onClick={resetChat}
                  data-testid="chat-send-another"
                  className="text-[#FF5E1A] text-sm font-medium hover:underline"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <input
                    type="text"
                    name="name"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="chat-name-input"
                    className="w-full bg-[#050B14] border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-[#5B6B8A] focus:border-[#FF5E1A] focus:ring-1 focus:ring-[#FF5E1A] outline-none transition-all"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    name="email"
                    placeholder="Your email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="chat-email-input"
                    className="w-full bg-[#050B14] border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-[#5B6B8A] focus:border-[#FF5E1A] focus:ring-1 focus:ring-[#FF5E1A] outline-none transition-all"
                  />
                </div>
                <div>
                  <textarea
                    name="message"
                    placeholder="How can we help?"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={3}
                    data-testid="chat-message-input"
                    className="w-full bg-[#050B14] border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-[#5B6B8A] focus:border-[#FF5E1A] focus:ring-1 focus:ring-[#FF5E1A] outline-none transition-all resize-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="chat-submit-btn"
                  className="w-full bg-[#FF5E1A] hover:bg-[#FF7A33] text-white font-semibold rounded-lg py-2.5 flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Send size={16} />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default LiveChat;
